import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router';
import { combineLatest, first } from 'rxjs';
import { MutatorState, useAppStorage } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { DrillDownWizard, drillDownWizardKey } from '../services/drillDown';
import { useLoading } from './useLoading';
import { GroupCompartments, groupStorageKey } from '../data/group';
import { GroupData } from '../models/GroupData';
import { TeamCompartments, teamStorageKey } from '../data/team';
import { TeamData } from '../models/TeamData';
import { OrganizationCompartments, organizationStorageKey } from '../data/organization';
import { OrganizationData } from '../models/OrganizationData';
import { updateCrumbs } from '../components/DrillDownBreadcrumbs';

interface UseRestorePreNavSelectedTiersParams {
  selectGroup: MutatorState<number>;
  selectTeam: MutatorState<number>;
  unselectOrganization: MutatorState<number>;
  unselectTeam: MutatorState<number>;
  unselectGroup: MutatorState<number>;
  setIsNavigatingToOrg: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useRestorePreNavSelectedTiers({
  selectGroup,
  selectTeam,
  unselectOrganization,
  unselectTeam,
  unselectGroup,
  setIsNavigatingToOrg,
}: UseRestorePreNavSelectedTiersParams) {
  const navigationType = useNavigationType();
  const location = useLocation();
  const { setLoading } = useLoading();
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const appStorage = useAppStorage();

  // location.key dependency ensures we run once per navigation — avoids relying on stale navigationType.
  // first() completes the cache subscription once the org compartment is initialized —
  // otherwise selecting a subsequent org would fire the restore again and immediately unselect it.
  useEffect(() => {
    if (navigationType !== 'POP') {
      return;
    }

    const groupDataCache = appStorage.retrieve<GroupCompartments>(groupStorageKey);
    const teamDataCache = appStorage.retrieve<TeamCompartments>(teamStorageKey);
    const orgDataCache = appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);

    const subscription = combineLatest([
      orgDataCache.findCompartment('organization').initialized$,
      orgDataCache.observe$<OrganizationData>('organization'),
      teamDataCache.observe$<TeamData>('team'),
      groupDataCache.observe$<GroupData>('group'),
    ])
      .pipe(first(([orgInitialized]) => orgInitialized))
      .subscribe(([, orgData, teamData, groupData]) => {
        const orgId = orgData?.id;

        if (!orgId) {
          return;
        }

        const prevState = drillDownWizard.popPreNavSelectedTiers();

        if (prevState) {
          const { groupId, teamId } = prevState;
          setLoading(true);
          setIsNavigatingToOrg(true);

          const restorePreNavSelectedTiers = async () => {
            try {
              await unselectOrganization.action(orgId);

              if (!teamId && teamData?.id) {
                await unselectTeam.action(teamData.id);
              }

              if (groupId) await selectGroup.action(groupId);
              if (teamId) await selectTeam.action(teamId);
            } catch (e) {
              console.error('Failed to restore pre-nav selected tiers', e);
            } finally {
              setIsNavigatingToOrg(false);
              setLoading(false);
            }
          };

          restorePreNavSelectedTiers();
        } else {
          setLoading(true);
          updateCrumbs({
            groupData,
            teamData,
            orgData,
            unselectGroup,
            unselectTeam,
            unselectOrganization,
          }).finally(() => setLoading(false));
        }
      });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
}
